import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Modal from './Modal';
import Toast from './Toast';
import useFormCRUD from '../hooks/useFormCRUD';

/**
 * Budget management form for the Setup page.
 * Provides CRUD operations for budget entries scoped by business, vendor, and/or category.
 * Budgets define spending targets per period (monthly/quarterly/yearly).
 *
 * @param {Object} props
 * @param {Function} [props.onUpdate] - Callback invoked after create/update/delete operations
 * @returns {JSX.Element} The budget management form with table
 */
function BudgetForm({ onUpdate }) {
  /**
   * Load reference data (businesses, vendors, categories) for dropdowns.
   *
   * @returns {Promise<Object>} Object with businesses, vendors, categories arrays
   */
  const loadRefData = useCallback(async () => {
    const [bizRes, vendRes, catRes] = await Promise.all([
      api.getBusinesses(),
      api.getVendors(),
      api.getCategories ? api.getCategories() : Promise.resolve({ data: [] }),
    ]);
    return {
      businesses: bizRes.data || [],
      vendors: vendRes.data || [],
      categories: catRes.data || [],
    };
  }, []);

  const {
    items: budgets,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    referenceData,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleCloseToast,
  } = useFormCRUD({
    apiGetAll: api.getBudgets,
    apiCreate: api.createBudget,
    apiUpdate: api.updateBudget,
    apiDelete: api.deleteBudget,
    initialFormData: {
      business_id: '',
      vendor_id: '',
      category_id: '',
      period_type: 'monthly',
      period_start: '',
      amount: '',
      notes: ''
    },
    entityName: 'Budget',
    useConfirmModal: false,
    loadReferenceData: loadRefData,
    mapEditData: (b) => ({
      business_id: String(b.business_id || ''),
      vendor_id: String(b.vendor_id || ''),
      category_id: String(b.category_id || ''),
      period_type: b.period_type || 'monthly',
      period_start: b.period_start || '',
      amount: String(b.amount || ''),
      notes: b.notes || '',
    }),
    validateSubmitData: (data) => {
      if (!data.business_id) return 'Please select a business/location';
      if (!data.amount || parseFloat(data.amount) <= 0) return 'Please enter a valid budget amount';
      if (!data.period_start) return 'Please select a period start date';
      return null;
    },
    transformSubmitData: (data) => ({
      business_id: parseInt(data.business_id, 10),
      vendor_id: data.vendor_id ? parseInt(data.vendor_id, 10) : null,
      category_id: data.category_id ? parseInt(data.category_id, 10) : null,
      period_type: data.period_type,
      period_start: data.period_start,
      amount: parseFloat(data.amount),
      notes: data.notes?.trim() || '',
    }),
    onUpdate,
  });

  const businesses = referenceData.businesses || [];
  const vendors = referenceData.vendors || [];
  const categories = referenceData.categories || [];

  /**
   * Build a human-readable label for a budget's scope.
   *
   * @param {Object} budget - Budget object with vendor_name and category_path
   * @returns {string} Label like "Sysco — Proteins > Chicken" or "All Vendors — All Categories"
   */
  const getBudgetLabel = (budget) => {
    const vendor = budget.vendor_name || 'All Vendors';
    const category = budget.category_path || 'All Categories';
    return `${vendor} — ${category}`;
  };

  /**
   * Format a currency amount with $ sign and commas.
   *
   * @param {number} amount - Dollar amount
   * @returns {string} Formatted string like "$15,000.00"
   */
  const formatCurrency = (amount) => {
    return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  /**
   * Build a category path string from a category object.
   *
   * @param {Object} cat - Category with level1, level2, level3
   * @returns {string} Path like "Proteins > Chicken > Breast"
   */
  const getCategoryPath = (cat) => {
    return [cat.level1, cat.level2, cat.level3].filter(Boolean).join(' > ');
  };

  /**
   * Render the budget form fields (shared between Add and Edit modals).
   *
   * @param {string} idPrefix - Prefix for field IDs to avoid duplicates ('add' or 'edit')
   * @returns {JSX.Element} The form fields JSX
   */
  const renderFormFields = (idPrefix) => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-business`}>Business / Location *</label>
          <select
            id={`${idPrefix}-budget-business`}
            value={formData.business_id}
            onChange={(e) => setFormData({ ...formData, business_id: e.target.value })}
            required
          >
            <option value="">Select business...</option>
            {businesses.filter(b => b.status === 'active').map(b => (
              <option key={b.id} value={b.id}>
                {b.parent_id ? '  \u2514 ' : ''}{b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-vendor`}>Vendor (optional)</label>
          <select
            id={`${idPrefix}-budget-vendor`}
            value={formData.vendor_id}
            onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
          >
            <option value="">All Vendors</option>
            {vendors.filter(v => v.status === 'active').map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-category`}>Category (optional)</label>
          <select
            id={`${idPrefix}-budget-category`}
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
          >
            <option value="">All Categories</option>
            {categories.filter(c => c.status === 'active').map(c => (
              <option key={c.id} value={c.id}>{getCategoryPath(c)}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-period-type`}>Period Type *</label>
          <select
            id={`${idPrefix}-budget-period-type`}
            value={formData.period_type}
            onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-period-start`}>Period Start *</label>
          <input
            id={`${idPrefix}-budget-period-start`}
            type="date"
            value={formData.period_start}
            onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-budget-amount`}>Budget Amount ($) *</label>
          <input
            id={`${idPrefix}-budget-amount`}
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g. 15000"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-budget-notes`}>Notes (optional)</label>
        <input
          id={`${idPrefix}-budget-notes`}
          type="text"
          placeholder="e.g. Q1 protein target"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>
    </>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h2>Budgets</h2>
        <button
          className="btn-small btn-primary"
          onClick={() => { handleCancelEdit(); setShowForm(!showForm); }}
        >
          {showForm && !editingId ? 'Cancel' : '+ Add Budget'}
        </button>
      </div>

      {/* Add Budget Modal */}
      <Modal
        isOpen={showForm && !editingId}
        onClose={() => setShowForm(false)}
        title="Add Budget"
        size="medium"
      >
        <form onSubmit={(e) => { handleSubmit(e); }} className="budget-form__wrapper">
          {renderFormFields('add')}
          <div className="form-actions--spaced">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Add Budget'}
            </button>
            <button type="button" className="btn-outline" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Budget Modal */}
      <Modal
        isOpen={!!editingId}
        onClose={() => { handleCancelEdit(); setShowForm(false); }}
        title="Edit Budget"
        size="medium"
      >
        <form onSubmit={handleSubmit} className="budget-form__wrapper">
          {renderFormFields('edit')}
          <div className="form-actions--spaced">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Update Budget'}
            </button>
            <button type="button" className="btn-outline" onClick={() => { handleCancelEdit(); setShowForm(false); }}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {budgets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="empty-state__title">No budgets configured</div>
          <div className="empty-state__description">
            Add a budget to start tracking your spending against targets. Set budgets per category, vendor, or location.
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Scope</th>
                <th>Period</th>
                <th>Start</th>
                <th className="text-right">Amount</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {budgets.map((budget) => (
                <tr key={budget.id} className={editingId === budget.id ? 'row-highlight' : ''}>
                  <td>{getBudgetLabel(budget)}</td>
                  <td className="budget-form__period-type">{budget.period_type}</td>
                  <td>{budget.period_start}</td>
                  <td className="text-right">{formatCurrency(budget.amount)}</td>
                  <td>{budget.notes || '—'}</td>
                  <td>
                    <div className="budget-form__action-buttons">
                      <button
                        className="btn-small btn-outline"
                        onClick={() => handleEdit(budget)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDelete(budget.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={handleCloseToast} />}
    </div>
  );
}

BudgetForm.propTypes = {
  /** Callback invoked after create/update/delete operations */
  onUpdate: PropTypes.func,
};

export default BudgetForm;
